'use client';

import 'katex/dist/katex.min.css';

import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import type { JSONContent } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';

// --- Tiptap Core Extensions ---
import { StarterKit } from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { TextAlign } from '@tiptap/extension-text-align';
import { Typography } from '@tiptap/extension-typography';
import { Highlight } from '@tiptap/extension-highlight';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { CharacterCount, Selection } from '@tiptap/extensions';
import Underline from '@tiptap/extension-underline';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell as BaseTableCell } from '@tiptap/extension-table-cell';
import { MathematicsInlineEdit } from '@/components/tiptap-extensions/mathematics-inline-edit';
import { MermaidDiagram } from '@/components/tiptap-extensions/mermaid-block';
import Mention from '@tiptap/extension-mention';
import { Markdown } from '@tiptap/markdown';
import Link from '@tiptap/extension-link';
import { SensitiveDataMark } from '../../../../src/components/Workspace/canvas/editors/SensitiveDataMark';

// --- UI Primitives ---
import { Button } from '@/components/tiptap-ui-primitive/button';
import { Spacer } from '@/components/tiptap-ui-primitive/spacer';
import { Toolbar, ToolbarGroup, ToolbarSeparator } from '@/components/tiptap-ui-primitive/toolbar';

// --- Tiptap Node ---
import { ImageUploadNode } from '@/components/tiptap-node/image-upload-node/image-upload-node-extension';
import { HorizontalRule } from '@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension';
import '@/components/tiptap-node/blockquote-node/blockquote-node.scss';
import '@/components/tiptap-node/code-block-node/code-block-node.scss';
import '@/components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss';
import '@/components/tiptap-node/list-node/list-node.scss';
import { ImageNodeView } from '@/components/tiptap-node/image-node/image-node-view';
import '@/components/tiptap-node/image-node/image-node.scss';
import '@/components/tiptap-node/heading-node/heading-node.scss';
import '@/components/tiptap-node/paragraph-node/paragraph-node.scss';
import '@/components/tiptap-extensions/mathematics-inline-edit.scss';

// --- Tiptap UI ---
import { HeadingDropdownMenu } from '@/components/tiptap-ui/heading-dropdown-menu';
import { ImageUploadButton } from '@/components/tiptap-ui/image-upload-button';
import { ListDropdownMenu } from '@/components/tiptap-ui/list-dropdown-menu';
import { BlockquoteButton } from '@/components/tiptap-ui/blockquote-button';
import { CodeBlockButton } from '@/components/tiptap-ui/code-block-button';
import {
  ColorHighlightPopover,
  ColorHighlightPopoverContent,
  ColorHighlightPopoverButton,
} from '@/components/tiptap-ui/color-highlight-popover';
import { LinkPopover, LinkContent, LinkButton } from '@/components/tiptap-ui/link-popover';
import { MarkButton } from '@/components/tiptap-ui/mark-button';
import { TextAlignButton } from '@/components/tiptap-ui/text-align-button';
import { UndoRedoButton } from '@/components/tiptap-ui/undo-redo-button';

// --- Icons ---
import { ArrowLeftIcon } from '@/components/tiptap-icons/arrow-left-icon';
import { HighlighterIcon } from '@/components/tiptap-icons/highlighter-icon';
import { LinkIcon } from '@/components/tiptap-icons/link-icon';

// --- Hooks ---
import { useIsBreakpoint } from '@/hooks/use-is-breakpoint';
import { useWindowSize } from '@/hooks/use-window-size';
import { useCursorVisibility } from '@/hooks/use-cursor-visibility';

// --- Components ---

// --- Lib ---
import { handleImageUpload, MAX_FILE_SIZE } from '@/lib/tiptap-utils';

// --- Styles ---
import '@/components/tiptap-templates/simple/simple-editor.scss';

import defaultContent from '@/components/tiptap-templates/simple/data/content.json';

// --- Table UI (custom button) ---
import { TableButton } from '@/components/tiptap-ui/table-button';
import { MathButton } from '@/components/tiptap-ui/math-button';
import { BlockMathButton } from '@/components/tiptap-ui/block-math-button/block-math-button';
import { MermaidButton } from '@/components/tiptap-ui/mermaid-button';

export type SimpleEditorValue = JSONContent;

export type SimpleEditorProps = {
  /**
   * Optional TipTap JSON document to load.
   * When omitted, the template demo content is used.
   */
  value?: SimpleEditorValue | null;
  /**
   * Called whenever the document changes.
   */
  onChange?: (value: SimpleEditorValue) => void;
  /**
   * Toggle read-only mode. Defaults to false (editable).
   */
  readOnly?: boolean;
  /**
   * When set, image uploads go to media/user/<imageUploadDocStem>/images (same as OCR).
   * Pass the document artifact id so uploads are stored with the document.
   */
  imageUploadDocStem?: string;
  /** Called when character/word count changes (e.g. for footer display). */
  onCharacterCountChange?: (counts: { characters: number; words: number }) => void;
};

// Keep table attributes compatible with the workspace editors and OCR converter.
// This mirrors the configuration used in:
// - app/backend/tiptap_html_convert/cli.mjs
// - app/frontend/src/components/Workspace/canvas/editors/RichMarkdownEditor.tsx
const TableExtended = Table.extend({
  addAttributes() {
    return {
      // Preserve any parent attributes configured by the base extension.
      ...this.parent?.(),
      borderStyle: {
        // NOTE: We intentionally keep the default as null so TipTap still
        // stores the attribute, but we ALWAYS render a data-border attribute
        // so CSS can reliably target the grid even for default tables.
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-border') || 'grid',
        renderHTML: (attributes: Record<string, unknown>) => ({
          'data-border': (attributes.borderStyle as string | null) ?? 'grid',
        }),
      },
    };
  },
});

// Allow per-cell background color via setCellAttribute('backgroundColor', …).
const TableCell = BaseTableCell.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      backgroundColor: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-bg') || null,
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes.backgroundColor
            ? {
                'data-bg': attributes.backgroundColor,
                style: `background-color: ${attributes.backgroundColor}`,
              }
            : {},
      },
    };
  },
});

// Image with scaling (width %, drag handles) and alignment (toolbar left/center/right).
const ImageExtended = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-width') || null,
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes.width ? { 'data-width': String(attributes.width) } : {},
      },
      align: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-align') || null,
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes.align ? { 'data-align': attributes.align as string } : {},
      },
      positionPct: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const v = element.getAttribute('data-position-pct');
          return v != null ? parseFloat(v) : null;
        },
        renderHTML: (attributes: Record<string, unknown>) =>
          attributes.positionPct != null
            ? { 'data-position-pct': String(attributes.positionPct) }
            : {},
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageNodeView);
  },
});

const MainToolbarContent = ({
  onHighlighterClick,
  onLinkClick,
  isMobile,
}: {
  onHighlighterClick: () => void;
  onLinkClick: () => void;
  isMobile: boolean;
}) => {
  return (
    <>
      <Spacer />

      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4]} portal={isMobile} />
        <ListDropdownMenu types={['bulletList', 'orderedList', 'taskList']} portal={isMobile} />
        <BlockquoteButton />
        <CodeBlockButton />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="code" />
        <MarkButton type="underline" />
        {!isMobile ? (
          <ColorHighlightPopover />
        ) : (
          <ColorHighlightPopoverButton onClick={onHighlighterClick} />
        )}
        {!isMobile ? <LinkPopover /> : <LinkButton onClick={onLinkClick} />}
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="superscript" />
        <MarkButton type="subscript" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <TableButton />
        <MathButton />
        <BlockMathButton />
        <MermaidButton />
        <ImageUploadButton />
      </ToolbarGroup>

      <Spacer />
    </>
  );
};

const MobileToolbarContent = ({
  type,
  onBack,
}: {
  type: 'highlighter' | 'link';
  onBack: () => void;
}) => (
  <>
    <ToolbarGroup>
      <Button data-style="ghost" onClick={onBack}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === 'highlighter' ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === 'highlighter' ? <ColorHighlightPopoverContent /> : <LinkContent />}
  </>
);

function reportCharacterCount(
  editor: Editor,
  cb: ((counts: { characters: number; words: number }) => void) | undefined,
) {
  if (!cb) return;
  const storage = editor.storage?.characterCount;
  if (!storage?.characters || !storage?.words) return;
  cb({ characters: storage.characters(), words: storage.words() });
}

export function SimpleEditor({
  value,
  onChange,
  readOnly = false,
  imageUploadDocStem,
  onCharacterCountChange,
}: SimpleEditorProps) {
  const isMobile = useIsBreakpoint();
  const { height } = useWindowSize();
  const [mobileView, setMobileView] = useState<'main' | 'highlighter' | 'link'>('main');
  const toolbarRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<Editor | null>(null);
  const onCharacterCountChangeRef = useRef(onCharacterCountChange);
  onCharacterCountChangeRef.current = onCharacterCountChange;

  // Keep a serialized copy of the last value we emitted, so that
  // we can avoid infinite loops when syncing external state.
  const lastValueRef = useRef<string | null>(null);

  const initialContent: SimpleEditorValue = useMemo(() => {
    // If value is explicitly provided (even if empty), use it
    if (value && typeof value === 'object') {
      return value;
    }
    // Otherwise, use empty doc for blank slate (not the demo content)
    return { type: 'doc', content: [] } as SimpleEditorValue;
  }, [value]);

  const editor = useEditor(
    {
      immediatelyRender: false,
      editorProps: {
        attributes: {
          autocomplete: 'off',
          autocorrect: 'off',
          autocapitalize: 'off',
          'aria-label': 'Main content area, start typing to enter text.',
          class: 'simple-editor',
        },
      },
      extensions: [
        // Core document schema & behavior
        StarterKit.configure({
          horizontalRule: false,
          // Disable built-in link so we can use the dedicated Link extension
          link: false,
          // Disable so we register Underline once explicitly (avoids duplicate extension warning)
          underline: false,
        }),
        HorizontalRule,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Highlight.configure({ multicolor: true }),
        ImageExtended,
        Typography,
        Superscript,
        Subscript,
        CharacterCount,
        Selection,
        Underline,
        Link.configure({
          openOnClick: false,
          enableClickSelection: true,
        }),
        // Sensitive data highlighting mark (acts like bold/italic for sensitive spans)
        SensitiveDataMark,

        // Tables – keep attributes in sync with OCR converter & RichMarkdownEditor
        TableExtended.configure({
          HTMLAttributes: { class: 'tiptap-editor-table' },
          resizable: true,
          lastColumnResizable: true,
        }),
        TableRow,
        TableHeader.configure({
          HTMLAttributes: { class: 'tiptap-editor-header' },
        }),
        TableCell.configure({
          HTMLAttributes: { class: 'tiptap-editor-cell' },
        }),
        // Math – inline editing extension; click formula to edit inline LaTeX
        MathematicsInlineEdit.configure({
          katexOptions: { throwOnError: false },
        }),
        // Mermaid diagrams – block-level node with inline editing
        MermaidDiagram,
        // Markdown import/export helpers
        Markdown.configure({}),
        // Simple mention chip support (static list elsewhere)
        Mention.configure({
          HTMLAttributes: {
            class: 'mention-chip',
          },
        }),
        // Image upload node: store under media/user/<imageUploadDocStem>/images (same as OCR)
        ImageUploadNode.configure({
          accept: 'image/*',
          maxSize: MAX_FILE_SIZE,
          limit: 3,
          upload: (file, onProgress, abortSignal) =>
            handleImageUpload(file, onProgress, abortSignal, {
              docStem: imageUploadDocStem ?? 'tiptap-editor',
            }),
          onError: (error) => console.error('Upload failed:', error),
        }),
      ],
      content: initialContent,
      editable: !readOnly,
      onUpdate: ({ editor }) => {
        const json = editor.getJSON() as SimpleEditorValue;
        const serialized = JSON.stringify(json);
        if (onChange && serialized !== lastValueRef.current) {
          lastValueRef.current = serialized;
          onChange(json);
        }
        reportCharacterCount(editor, onCharacterCountChangeRef.current);
      },
      onCreate: ({ editor }) => {
        editorRef.current = editor;
        reportCharacterCount(editor, onCharacterCountChangeRef.current);
      },
    },
    [readOnly, imageUploadDocStem],
  );

  // Keep the editor in sync when the caller supplies a new value.
  // IMPORTANT: For editable documents in the workspace we treat the editor
  // as the source of truth and DO NOT continuously re-hydrate from the
  // external `value` prop, because backend-normalized JSON can differ
  // slightly and cause cursor jumps / content resets.
  //
  // We only sync from `value` when the editor is read-only, so it can act
  // as a pure viewer for external content.
  useEffect(() => {
    if (!readOnly) return;
    if (!editor || !value || typeof value !== 'object') return;
    const serialized = JSON.stringify(value);
    if (serialized === lastValueRef.current) {
      return;
    }
    lastValueRef.current = serialized;
    editor.commands.setContent(value as SimpleEditorValue, { emitUpdate: false });
  }, [editor, value, readOnly]);

  const rect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  });

  useEffect(() => {
    if (!isMobile && mobileView !== 'main') {
      setMobileView('main');
    }
  }, [isMobile, mobileView]);

  const tableBorder: 'grid' | 'rows' | 'cols' | 'none' =
    (editor?.isActive('table') &&
      (editor?.getAttributes('table')?.borderStyle as
        | 'grid'
        | 'rows'
        | 'cols'
        | 'none'
        | undefined)) ||
    'grid';

  return (
    <div className="simple-editor-wrapper">
      <EditorContext.Provider value={{ editor }}>
        <Toolbar
          ref={toolbarRef}
          style={{
            ...(isMobile
              ? {
                  bottom: `calc(100% - ${height - rect.y}px)`,
                }
              : {}),
          }}
        >
          {mobileView === 'main' ? (
            <MainToolbarContent
              onHighlighterClick={() => setMobileView('highlighter')}
              onLinkClick={() => setMobileView('link')}
              isMobile={isMobile}
            />
          ) : (
            <MobileToolbarContent
              type={mobileView === 'highlighter' ? 'highlighter' : 'link'}
              onBack={() => setMobileView('main')}
            />
          )}
        </Toolbar>

        <EditorContent editor={editor} role="presentation" className="simple-editor-content" />

        {editor && (
          <BubbleMenu
            editor={editor}
            shouldShow={({ editor: ed }: { editor: Editor }) => ed.isActive('table')}
            className="tiptap-table-bubble-menu"
            options={{ placement: 'top', offset: 8 }}
            getReferencedVirtualElement={() => {
              const { state, view } = editor;
              const { from } = state.selection;
              const domAtPos = view.domAtPos(from);
              const rawNode = (domAtPos as any).node ?? domAtPos.node;
              const element =
                rawNode instanceof HTMLElement ? rawNode : (rawNode?.parentElement ?? null);
              const table = element?.closest('table') as HTMLElement | null;
              if (!table) return null;

              // Get the natural table rect.
              const tableRect = table.getBoundingClientRect();

              // Clamp the reference so it never scrolls above the bottom of the main toolbar.
              // This makes the menu follow the table while it's below the toolbar,
              // then "stick" once the table scrolls behind the toolbar.
              const toolbar = toolbarRef.current;
              const toolbarRect = toolbar?.getBoundingClientRect();
              const toolbarBottom = toolbarRect ? toolbarRect.bottom : 0;

              const top = Math.max(tableRect.top, toolbarBottom);

              return {
                getBoundingClientRect: () =>
                  ({
                    width: tableRect.width,
                    height: tableRect.height,
                    top,
                    bottom: top + tableRect.height,
                    left: tableRect.left,
                    right: tableRect.right,
                    x: tableRect.left,
                    y: top,
                  }) as DOMRect,
              };
            }}
          >
            {/* Rows group */}
            <div className="tiptap-table-bubble-menu-group">
              <div className="tiptap-table-bubble-menu-group-buttons">
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Add row after"
                  tooltip="Add row after"
                  onClick={() => editor.chain().focus().addRowAfter().run()}
                >
                  +
                </Button>
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Delete current row"
                  tooltip="Delete current row"
                  onClick={() => editor.chain().focus().deleteRow().run()}
                >
                  −
                </Button>
              </div>
              <span className="tiptap-table-bubble-menu-group-label">Rows</span>
            </div>

            {/* Separator between groups */}
            <div className="tiptap-separator" data-orientation="vertical" role="none" />

            {/* Columns group */}
            <div className="tiptap-table-bubble-menu-group">
              <div className="tiptap-table-bubble-menu-group-buttons">
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Add column after"
                  tooltip="Add column after"
                  onClick={() => editor.chain().focus().addColumnAfter().run()}
                >
                  +
                </Button>
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Delete current column"
                  tooltip="Delete current column"
                  onClick={() => editor.chain().focus().deleteColumn().run()}
                >
                  −
                </Button>
              </div>
              <span className="tiptap-table-bubble-menu-group-label">Columns</span>
            </div>

            {/* Header group */}
            <div className="tiptap-separator" data-orientation="vertical" role="none" />
            <div className="tiptap-table-bubble-menu-group">
              <div className="tiptap-table-bubble-menu-group-buttons">
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Toggle header row"
                  tooltip="Toggle header row"
                  onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                >
                  Row
                </Button>
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Toggle header column"
                  tooltip="Toggle header column"
                  onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
                >
                  Col
                </Button>
              </div>
              <span className="tiptap-table-bubble-menu-group-label">Headers</span>
            </div>

            {/* Cell group:  merge / shading */}
            <div className="tiptap-separator" data-orientation="vertical" role="none" />
            <div className="tiptap-table-bubble-menu-group">
              <div className="tiptap-table-bubble-menu-group-buttons">
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Merge or split cells"
                  tooltip="Merge or split cells"
                  onClick={() => editor.chain().focus().mergeOrSplit().run()}
                >
                  M/S
                </Button>
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Shade cell"
                  tooltip="Shade cell"
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .setCellAttribute('backgroundColor', 'var(--tt-editor-table-cell-shade-bg)')
                      .run()
                  }
                >
                  Shade
                </Button>
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Clear cell shading"
                  tooltip="Clear cell shading"
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .setCellAttribute('backgroundColor', null as any)
                      .run()
                  }
                >
                  Clear
                </Button>
              </div>
              <span className="tiptap-table-bubble-menu-group-label">Cells</span>
            </div>

            {/* Fix / borders group */}
            <div className="tiptap-separator" data-orientation="vertical" role="none" />
            <div className="tiptap-table-bubble-menu-group">
              <div className="tiptap-table-bubble-menu-group-buttons">
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Fix tables"
                  tooltip="Fix tables"
                  onClick={() => editor.chain().focus().fixTables().run()}
                >
                  Fix
                </Button>
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Show grid lines"
                  tooltip="Grid lines"
                  data-active={tableBorder === 'grid'}
                  onClick={() =>
                    editor.chain().focus().updateAttributes('table', { borderStyle: 'grid' }).run()
                  }
                >
                  Grid
                </Button>
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Show row separators only"
                  tooltip="Rows only"
                  data-active={tableBorder === 'rows'}
                  onClick={() =>
                    editor.chain().focus().updateAttributes('table', { borderStyle: 'rows' }).run()
                  }
                >
                  Rows
                </Button>
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Show column separators only"
                  tooltip="Columns only"
                  data-active={tableBorder === 'cols'}
                  onClick={() =>
                    editor.chain().focus().updateAttributes('table', { borderStyle: 'cols' }).run()
                  }
                >
                  Cols
                </Button>
                <Button
                  type="button"
                  data-style="ghost"
                  aria-label="Hide table lines"
                  tooltip="No lines"
                  data-active={tableBorder === 'none'}
                  onClick={() =>
                    editor.chain().focus().updateAttributes('table', { borderStyle: 'none' }).run()
                  }
                >
                  None
                </Button>
              </div>
              <span className="tiptap-table-bubble-menu-group-label">Borders</span>
            </div>
          </BubbleMenu>
        )}
      </EditorContext.Provider>
    </div>
  );
}
